import React, { useState, useMemo, useEffect } from "react";
import { BaseUrl } from "../../../constants/BaseURL";

const GOOGLE_FONTS_LINK =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;600;700&display=swap";

export default function EmbeddedProductSettings({ id }) {
  const [s, setS] = useState({
    primary: "#2d6ef7",
    cardBg: "#ffffff",
    priceBadge: "#212529",
    priceColour: "#ffffff",
    bgColour: "#f2f2f2",
    font: "Poppins",
    baseSize: 16,
    headingWeight: 600,
    radius: 12,
    shadow: "sm",
    btnShape: "rounded",
    btnSize: "md",
    headerAlign: "center",
    btnTextColour: "#ffffff",
    btnHover: "#1e58d6",
    btnTextHover: "#ffffff",
  });

  useEffect(() => {
    const already = [...document.styleSheets].some(
      (s) => s.href && s.href.includes("fonts.googleapis.com/css2")
    );
    if (!already) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = GOOGLE_FONTS_LINK;
      document.head.appendChild(link);
    }
  }, []);

  const upd = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));

  const iframeSrc = useMemo(() => {
    const base = `${BaseUrl}/WidgitProducts/${id}`;
    const q = new URLSearchParams({
      primary: s.primary,
      cardBg: s.cardBg,
      priceBadge: s.priceBadge,
      priceColour: s.priceColour,
      bgColour: s.bgColour,
      font: s.font,
      baseSize: String(s.baseSize),
      headingWeight: String(s.headingWeight),
      radius: String(s.radius),
      shadow: s.shadow,
      btnShape: s.btnShape,
      btnSize: s.btnSize,
      headerAlign: s.headerAlign,
      btnTextColour: s.btnTextColour,
      btnHover: s.btnHover,
      btnTextHover: s.btnTextHover,
    });
    return `${base}?${q.toString()}`;
  }, [s, id]);

  const embedCode = `<iframe src="${iframeSrc}" width="100%" height="700" frameborder="0" loading="lazy" allowfullscreen></iframe>`;

  return (
    <div>
      <form className="p-4">
        <h4 className="mb-3">Branding</h4>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Button colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.primary}
              onChange={upd("primary")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Button text colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.btnTextColour}
              onChange={upd("btnTextColour")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Button colour on hover</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.btnHover}
              onChange={upd("btnHover")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Button text on hover</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.btnTextHover}
              onChange={upd("btnTextHover")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Card background colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.cardBg}
              onChange={upd("cardBg")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Price badge colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.priceBadge}
              onChange={upd("priceBadge")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Price text colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.priceColour}
              onChange={upd("priceColour")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Background colour</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={s.bgColour === "transparent" ? "#f2f2f2" : s.bgColour}
              onChange={upd("bgColour")}
              disabled={s.bgColour === "transparent"}
            />
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={s.bgColour === "transparent"}
                onChange={(e) =>
                  setS((x) => ({
                    ...x,
                    bgColour: e.target.checked ? "transparent" : "#f2f2f2",
                  }))
                }
              />
              <label className="form-check-label">Transparent background</label>
            </div>
          </div>
        </div>

        <h4 className="mt-4 mb-3">Typography</h4>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Font family</label>
            <select className="form-select" value={s.font} onChange={upd("font")}>
              <option value="Inter">Inter</option>
              <option value="Arial">Arial</option>
              <option value="Roboto">Roboto</option>
              <option value="Poppins">Poppins</option>
              <option value="Georgia">Georgia</option>
              <option value="Lato">Lato</option>
              <option value="Open Sans">Open Sans</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Nunito">Nunito</option>
              <option value="Merriweather">Merriweather</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Base font size</label>
            <input
              type="number"
              className="form-control"
              min="12"
              max="24"
              value={s.baseSize}
              onChange={upd("baseSize")}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Heading weight</label>
            <select
              className="form-select"
              value={s.headingWeight}
              onChange={upd("headingWeight")}
            >
              <option value={500}>500</option>
              <option value={600}>600</option>
              <option value={700}>700</option>
            </select>
          </div>
        </div>

        <h4 className="mt-4 mb-3">Layout</h4>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Card radius</label>
            <input
              type="number"
              className="form-control"
              min="0"
              max="30"
              value={s.radius}
              onChange={upd("radius")}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Card shadow</label>
            <select className="form-select" value={s.shadow} onChange={upd("shadow")}>
              <option value="none">none</option>
              <option value="sm">sm</option>
              <option value="md">md</option>
              <option value="lg">lg</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Button shape</label>
            <select className="form-select" value={s.btnShape} onChange={upd("btnShape")}>
              <option value="square">square</option>
              <option value="rounded">rounded</option>
              <option value="pill">pill</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Button size</label>
            <select className="form-select" value={s.btnSize} onChange={upd("btnSize")}>
              <option value="sm">sm</option>
              <option value="md">md</option>
              <option value="lg">lg</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Header alignment</label>
            <select
              className="form-select"
              value={s.headerAlign}
              onChange={upd("headerAlign")}
            >
              <option value="left">left</option>
              <option value="center">center</option>
              <option value="right">right</option>
            </select>
          </div>
        </div>
      </form>

      <iframe
        src={iframeSrc}
        className="w-100"
        height="700"
        loading="lazy"
        title="Riverside timetable"
        frameBorder="0"
        allowFullScreen
      />

      <div className="p-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-2">Embed Code</h5>
              <p className="text-muted small mb-2">Copy and paste this into your site.</p>
              <textarea className="form-control" rows={4} readOnly value={embedCode} />
              <div className="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigator.clipboard.writeText(embedCode)}
                >
                  Copy Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
